import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DocumentNode, ProcedureDocument } from '@completion/models';
import { Store } from '@ngrx/store';
import { selectEditMode } from '@completion/selectors';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { persistNodeUpdateForNode } from '../node-persistence.util';
import { parseLinkContentFromNode, ParsedLinkContent, toDataUri } from './link-parser.util';
import { PdfViewerDialogComponent } from './pdf-viewer-dialog/pdf-viewer-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  standalone: false,
})
export class LinkComponent implements OnInit, OnDestroy {
  documentNode: DocumentNode;
  procedureDocument: ProcedureDocument | null = null;
  editMode: boolean = false;

  // Local buffers for editing
  displayText: string = '';
  embedInAppendix: boolean = false;
  mimeType?: string;
  fileName?: string;
  // We will store base64 data in content JSON
  base64Data?: string;

  // Image meta fields
  altText: string = '';
  caption: string = '';
  /** Width as percent 10–100 */
  imageWidthPercent: number = 100;

  // parsed content object stored in documentNode.content (if any)
  contentObj: ParsedLinkContent | null = null;

  // Pending upload buffer; file is only persisted when user clicks save
  public pendingFile: File | null = null;
  pendingFileName: string | null = null;

  // Public helper for template binding to determine if there's a pending file
  public get hasPendingFile(): boolean {
    return !!this.pendingFile;
  }

  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    @Inject('documentNode') documentNode: DocumentNode,
    @Inject('node') procedureDocument: ProcedureDocument,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
  ) {
    this.documentNode = documentNode;
    this.procedureDocument = procedureDocument;

    // Use shared parser to extract structured content from the node (handles JSON content or top-level fields)
    try {
      const parsed = parseLinkContentFromNode(this.documentNode);
      this.contentObj = parsed;
      this.displayText = parsed.displayText || parsed.fileName || 'Link';
      this.mimeType = parsed.mimeType || undefined;
      this.fileName = parsed.fileName || undefined;
      this.base64Data = parsed.base64 || undefined;
      this.embedInAppendix = !!parsed.embedInAppendix;
      this.altText = parsed.altText || '';
      this.caption = parsed.caption || '';
      this.imageWidthPercent = parsed.imageWidth ? parseInt(parsed.imageWidth, 10) || 100 : 100;
    } catch {
      // Fallback to simple display
      this.displayText = String(this.documentNode?.content || 'Link');
      this.contentObj = { raw: this.documentNode } as ParsedLinkContent;
    }
  }

  ngOnInit(): void {
    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => (this.editMode = !!mode));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Safe URL for img/src and anchor href contexts */
  get dataUrl(): SafeUrl | null {
    if (!this.contentObj) return null;
    const data = toDataUri(this.contentObj.base64 || undefined, this.contentObj.mimeType || undefined);
    if (!data) return null;
    return this.sanitizer.bypassSecurityTrustUrl(data);
  }

  // Note: we intentionally do not provide a resource URL getter because PDFs are opened via a link in a new tab.

  // Helper: determine if the content is a PDF stored locally (base64)
  isPdfLocal(): boolean {
    return !!(
      this.contentObj &&
      this.contentObj.base64 &&
      this.contentObj.mimeType &&
      this.contentObj.mimeType.toLowerCase().indexOf('pdf') >= 0
    );
  }

  // Return a friendly size label if the parsed content contains a size in bytes (optional)
  getSizeLabel(): string | null {
    try {
      const size = this.contentObj?.size || null;
      if (!size) return null;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
      return `${Math.round(size / (1024 * 1024))} MB`;
    } catch {
      return null;
    }
  }

  // Return a human-friendly added date label if available
  getAddedDateLabel(): string | null {
    try {
      const d = this.contentObj?.addedDate || null;
      if (!d) return null;
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d);
      // Use locale date/time short format
      return dt.toLocaleString();
    } catch {
      return null;
    }
  }

  // Trigger a download of the content (base64 or secureUrl)
  downloadContent(): void {
    try {
      if (this.contentObj?.secureUrl) {
        const a = document.createElement('a');
        a.href = this.contentObj.secureUrl;
        a.download = this.contentObj.fileName || '';
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      if (this.contentObj?.base64 && this.contentObj.mimeType) {
        const blob = this.base64ToBlob(this.contentObj.base64, this.contentObj.mimeType);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.contentObj.fileName || '';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => {
          try {
            URL.revokeObjectURL(url);
          } catch {}
        }, 60_000);
      }
    } catch (e) {
      console.error('[LinkComponent] downloadContent failed', e);
    }
  }

  // Persist changes to content immutably (store JSON string in documentNode.content)
  private persistChanges(patch: Partial<any>): void {
    // Use shared helper: mutate the matching node with the patch and enqueue cloned document
    persistNodeUpdateForNode(this.store, this.updateQueue, this.documentNode, (n: any) => {
      Object.assign(n, patch);
    });
  }

  onDisplayTextChange(value: string): void {
    this.displayText = value || '';
    if (!this.contentObj) {
      this.contentObj = {};
    }
    this.contentObj.displayText = this.displayText;
    this.persistChanges({ content: JSON.stringify(this.contentObj) });
  }

  // Called on input to update local buffer without persisting
  // Accept raw input event to avoid template type errors and extract target value safely
  onDisplayTextInput(evt: Event): void {
    try {
      const val = (evt.target as HTMLInputElement)?.value;
      this.displayText = val ?? '';
    } catch {
      // fallback: if anything unexpected, do not change the buffer
    }
  }

  // Persist displayText (invoked by save button)
  saveDisplayText(): void {
    if (!this.contentObj) {
      this.contentObj = {};
    }
    this.contentObj.displayText = this.displayText || '';
    this.persistChanges({ content: JSON.stringify(this.contentObj) });
  }

  onAppendixToggle(checked: boolean): void {
    this.embedInAppendix = !!checked;
    if (!this.contentObj) {
      this.contentObj = {};
    }
    this.contentObj.embedInAppendix = this.embedInAppendix;
    this.persistChanges({ content: JSON.stringify(this.contentObj) });
  }

  // onFileChosen buffers the selected file without persisting it
  onFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      console.warn('Unsupported file type');
      return;
    }
    this.pendingFile = file;
    this.pendingFileName = file.name;
    try {
      input.value = '';
    } catch {}
  }

  // Persist the buffered file as content JSON (called by the save-upload button)
  async saveUpload(): Promise<void> {
    if (!this.pendingFile) return;
    const file = this.pendingFile;
    const base64 = await this.readAsBase64(file);
    this.mimeType = file.type;
    this.fileName = file.name;
    this.base64Data = base64;
    this.displayText = this.displayText || file.name;
    const addedDate = new Date().toISOString();
    this.contentObj = {
      displayText: this.displayText,
      mimeType: this.mimeType,
      fileName: this.fileName,
      base64: this.base64Data,
      embedInAppendix: this.embedInAppendix,
      size: file.size,
      addedDate,
      altText: this.altText || undefined,
      caption: this.caption || undefined,
      imageWidth: `${this.imageWidthPercent}%`,
    } as ParsedLinkContent;
    this.persistChanges({ content: JSON.stringify(this.contentObj) });
    // clear pending buffer
    this.pendingFile = null;
    this.pendingFileName = null;
  }

  // Cancel a pending buffered upload
  cancelUpload(): void {
    this.pendingFile = null;
    this.pendingFileName = null;
  }

  /** Persist image meta (altText, caption, imageWidth) for an already-saved image */
  saveImageMeta(): void {
    if (!this.contentObj) return;
    this.contentObj.altText = this.altText || undefined;
    this.contentObj.caption = this.caption || undefined;
    this.contentObj.imageWidth = `${this.imageWidthPercent}%`;
    this.persistChanges({ content: JSON.stringify(this.contentObj) });
  }

  /** Whether the current node holds an image attachment */
  get isImage(): boolean {
    return !!this.contentObj?.mimeType?.startsWith('image/');
  }

  /** CSS width string for image display */
  get imageWidthStyle(): string {
    return this.contentObj?.imageWidth || `${this.imageWidthPercent}%`;
  }

  // Convert a base64 string to a Blob
  private base64ToBlob(base64: string, mime: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
  }

  // Open the stored PDF in a new tab using a blob URL (works around data: URL navigation blocking)
  openPdfInNewTab(event: Event): void {
    try {
      event.preventDefault();
    } catch {}
    if (!this.contentObj) return;
    // If the server provided a secureUrl, prefer it
    const secure = this.contentObj?.secureUrl;
    if (secure && typeof secure === 'string') {
      try {
        const a = document.createElement('a');
        a.href = secure;
        a.target = '_blank';
        a.rel = 'noopener';
        // programmatic click in response to user gesture should open a new tab
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {
        try {
          window.open(secure, '_blank', 'noopener');
        } catch {
          console.warn('[LinkComponent] popup blocked when opening secureUrl');
        }
      }
      return;
    }
    if (!this.contentObj || !this.contentObj.base64 || !this.contentObj.mimeType) return;
    const mime = this.contentObj.mimeType;
    if (mime !== 'application/pdf') {
      // Not a PDF; nothing to do
      return;
    }
    try {
      const blob = this.base64ToBlob(this.contentObj.base64, mime);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {
        const w = window.open(url, '_blank', 'noopener');
        if (!w) console.warn('[LinkComponent] Failed to open PDF in new tab (popup blocked?)');
      }
      // Revoke the object URL after a delay to ensure the new tab had time to load
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }, 60_000);
    } catch (err) {
      console.error('[LinkComponent] openPdfInNewTab failed', err);
    }
  }

  // Open PDF viewer/editor dialog and persist edited PDF (base64) when saved
  async openPdfEditor(): Promise<void> {
    if (!this.contentObj) return;
    const dialogRef = this.dialog.open(PdfViewerDialogComponent, {
      minWidth: '1700px',
      height: '80%',
      data: { base64: this.contentObj.base64, fileName: this.contentObj.fileName },
    });
    const result = await dialogRef.afterClosed().toPromise();
    if (result && result.base64) {
      // Persist updated base64 into content JSON and enqueue update
      if (!this.contentObj) this.contentObj = {} as ParsedLinkContent;
      this.contentObj.base64 = result.base64;
      this.persistChanges({ content: JSON.stringify(this.contentObj) });
    }
  }

  // Click handler for the display-mode PDF label: prevent default navigation and open dialog
  onOpenPdfClick(event: Event): void {
    try {
      event.preventDefault();
      event.stopPropagation();
    } catch {}
    this.openPdfEditor();
  }

  private readAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        // result format: data:<mime>;base64,<data>
        const idx = result.indexOf('base64,');
        resolve(idx >= 0 ? result.substring(idx + 7) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}
